import { useContext, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import {
  CYRILLIC_REGEXP,
  getBankNameRegExp,
  getBicRegExp,
  SWIFT_ROUTING_CODE_REG_EXP
} from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import {
  getConveraRequirements,
  getRoutingCodeError,
  normalizeRoutingCode
} from 'components/common/PaymentForm/converaRequirements';
import i18nContext from 'components/i18n-context';
import ValidateBankDetailsModel from 'components/NewPaymentFormV2/models/ValidateBankDetailsModel';
import { getNewPaymentFormStepErrorMessage } from 'components/NewPaymentFormV2/utils/getNewPaymentFormStepErrorMessage';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './BankDetailsStep.scss';
import { PAYMENT_METHOD } from '../../../../../common/constants';

const BankDetailsStep = ({ newPaymentStore, customerNumber, userAccounts, userStore }) => {
  const i18n = useContext(i18nContext);

  // TRN-7466: a SWIFT beneficiary addressed by account number (no IBAN resolved on the account step) may need a
  // routing code (ABA for US, transit code for CA) — without it Convera refuses the payment with
  // "bankAccount.BranchCode". Optional, same field and format as the current form's Non-IBAN tab (TRN-7296).
  // TRN-7602: for Convera API / Merchant the Routing Code is MANDATORY for some bank countries (see
  // converaRequirements.js) — then it is shown (and validated: 4-11 letters and digits) even with an IBAN.
  const provider = newPaymentStore.currentAccount?.transfer_provider;
  const paymentMethod = newPaymentStore.paymentDetailsStep?.paymentMethod;
  const hasIban = !!newPaymentStore.accountDetailsStep?.iban;
  const getRoutingCodeState = (bankCountry) => {
    const { isRoutingCodeRequired } = getConveraRequirements({ provider, paymentMethod, bankCountry, hasIban });

    return {
      isRoutingCodeRequired,
      isRoutingCodeAvailable: paymentMethod === PAYMENT_METHOD.SWIFT && (!hasIban || isRoutingCodeRequired)
    };
  };

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      bankName: newPaymentStore.bankDetailsStep?.bankName || '',
      bankCountry: newPaymentStore.bankDetailsStep?.bankCountry || '',
      bic: newPaymentStore.bankDetailsStep?.bic || '',
      paymentPurpose: newPaymentStore.bankDetailsStep?.paymentPurpose || '',
      routingCode: newPaymentStore.bankDetailsStep?.routingCode || ''
    },
    validationSchema: Yup.object().shape({
      bankName: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(60, i18n.getMessage('client.validation.message.maxSize', { size: 60 }))
        .matches(getBankNameRegExp(), {
          message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
            availableSymbols: "“.“, “,“, “-“, “/“, “(“, “)“, “'“, “+“, “Space“"
          })
        }),
      bankCountry: Yup.string().trim().required(i18n.getMessage('client.validation.message.isEmpty')),
      bic: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(11, i18n.getMessage('client.validation.message.maxSize', { size: 11 }))
        .min(8, i18n.getMessage('client.validation.message.minSize', { size: 8 }))
        .matches(getBicRegExp(), {
          message: i18n.getMessage('client.validation.message.isOnlyUppercaseLatinLettersAndDigits')
        }),
      paymentPurpose: Yup.string().when('bankCountry', (bankCountry, schema) => {
        if (bankCountry === 'AE' && paymentPurposeOptions.length > 0) {
          return schema.trim().required(i18n.getMessage('client.validation.message.isEmpty'));
        }
        return schema;
      }),
      // `this.parent.bankCountry`: the rules follow the country as it is in the form now
      routingCode: Yup.string()
        .trim()
        .test('routing-code', function (value) {
          const { isRoutingCodeRequired, isRoutingCodeAvailable } = getRoutingCodeState(this.parent.bankCountry);

          if (isRoutingCodeRequired) {
            const routingCodeError = getRoutingCodeError(value);
            if (routingCodeError === 'empty') {
              return this.createError({ message: i18n.getMessage('client.validation.message.isEmpty') });
            }
            return routingCodeError
              ? this.createError({ message: i18n.getMessage('client.validation.message.routingCodeConvera') })
              : true;
          }

          if (isRoutingCodeAvailable && value && !SWIFT_ROUTING_CODE_REG_EXP.test(value)) {
            return this.createError({ message: i18n.getMessage('client.validation.message.routingCodeFormat') });
          }

          return true;
        })
    }),
    onSubmit: async (values) => {
      const { isRoutingCodeRequired, isRoutingCodeAvailable } = getRoutingCodeState(values.bankCountry);
      const formValues = {
        ...values,
        // Not applicable (e.g. the account step now resolved an IBAN): don't carry a stale value into the payment.
        // TRN-7602: "12-34-56" goes as "123456" (the validation ignores spaces and hyphens).
        routingCode: isRoutingCodeAvailable
          ? isRoutingCodeRequired
            ? normalizeRoutingCode(values.routingCode)
            : values.routingCode.trim()
          : ''
      };
      const requestData = ValidateBankDetailsModel({ ...newPaymentStore.bankDetailsStep, ...formValues });

      await newPaymentStore.validateBankDetails(customerNumber, requestData, formValues);
    }
  });

  const { values, errors, handleSubmit, handleChange } = form;
  const { isRoutingCodeRequired, isRoutingCodeAvailable } = getRoutingCodeState(values.bankCountry);

  // The rules follow the bank country at once: after a first submit attempt the form is validated again when the country
  // changes, so a field that became mandatory (or stopped being) shows / drops its error without another click.
  useEffect(() => {
    if (form.submitCount > 0) form.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.bankCountry]);

  const allCodes = userAccounts?.flatMap((acct) => acct.countries) ?? [];
  const uniqueCodes = [...new Set(allCodes)];
  const countriesOptions = uniqueCodes.map((code) => ({
    key: code,
    value: i18n.getMessage(`country.${code}`)
  }));

  const handleBankCountryChange = (name, data) => handleChange({ target: { name, value: data } });

  // Get provider type from current account
  const providerType = newPaymentStore.currentAccount?.transfer_provider;

  // Payment purpose options for UAE based on provider and country
  const paymentPurposeOptions = (() => {
    if (!providerType || values.bankCountry !== 'AE' || !userStore?.paymentPurposes?.length) {
      return [];
    }

    const providerData = userStore.paymentPurposes.find((item) => item.transferProvider === providerType);
    if (!providerData || !providerData.paymentPurposes) {
      return [];
    }

    const countryPurposes = providerData.paymentPurposes[values.bankCountry];
    if (!countryPurposes) {
      return [];
    }

    return Object.entries(countryPurposes).map(([key, value]) => ({
      key: key,
      value: `${key} - ${value}`
    }));
  })();

  const handlePaymentPurposeChange = (name, data) => handleChange({ target: { name, value: data } });

  // Check if UAE is selected and payment purposes are available
  const isUAESelected = values.bankCountry === 'AE' && paymentPurposeOptions.length > 0;

  return (
    <form onSubmit={form.handleSubmit} className={'new-payment-bank-details-step'}>
      <div className={'new-payment-bank-details-step-inputs-wrapper'}>
        <Input
          className={'new-payment-bank-details-step-input'}
          name={'bic'}
          label={i18n.getMessage('templates.form.bic.label')}
          placeholder={i18n.getMessage('templates.form.bic.placeholder')}
          value={values.bic}
          error={errors.bic || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'bic')}
          onChange={handleChange}
          isDisabled={
            !!(
              newPaymentStore?.newPaymentFormData?.paymentMethod === PAYMENT_METHOD.SWIFT &&
              newPaymentStore.bankDetailsPrefilledFields?.bic
            )
          }
          isRequired
        />
        <Input
          className={'new-payment-bank-details-step-input'}
          name={'bankName'}
          label={i18n.getMessage('templates.form.bankName.label')}
          placeholder={i18n.getMessage('templates.form.bankName.placeholder')}
          value={values.bankName}
          error={errors.bankName || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'bankName')}
          onChange={handleChange}
          isDisabled={
            !!(
              newPaymentStore?.newPaymentFormData?.paymentMethod === PAYMENT_METHOD.SWIFT &&
              newPaymentStore.bankDetailsPrefilledFields?.bankName
            )
          }
          isRequired
        />
        <InputDropDownSearch
          autoComplete={'off'}
          className={'new-payment-bank-details-step-input'}
          name={'bankCountry'}
          label={i18n.getMessage('templates.form.bankCountry.label')}
          placeholder={i18n.getMessage('templates.form.bankCountry.placeholder')}
          options={countriesOptions}
          value={values.bankCountry}
          error={errors.bankCountry || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'bankCountry')}
          onChange={handleBankCountryChange}
          isDisabled={
            !!(
              newPaymentStore?.newPaymentFormData?.paymentMethod === PAYMENT_METHOD.SWIFT &&
              newPaymentStore.bankDetailsPrefilledFields?.bankCountry
            )
          }
          isRequired
        />
        {isRoutingCodeAvailable && (
          <Input
            className={'new-payment-bank-details-step-input'}
            autoComplete={'off'}
            name={'routingCode'}
            label={i18n.getMessage('templates.form.routingCode.label')}
            placeholder={i18n.getMessage('templates.form.routingCode.placeholder')}
            value={values.routingCode}
            error={errors.routingCode}
            onChange={handleChange}
            isRequired={isRoutingCodeRequired}
          />
        )}
        {isUAESelected && (
          <InputDropDownSearch
            autoComplete={'off'}
            className={'new-payment-bank-details-step-input'}
            name={'paymentPurpose'}
            label={i18n.getMessage('templates.form.paymentPurpose.label')}
            placeholder={i18n.getMessage('templates.form.paymentPurpose.placeholder')}
            options={paymentPurposeOptions}
            value={values.paymentPurpose}
            error={
              errors.paymentPurpose || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'paymentPurpose')
            }
            onChange={handlePaymentPurposeChange}
            isRequired
          />
        )}
      </div>
      <div className={'new-payment-step-buttons'}>
        <Button
          className={'new-payment-bank-details-step-next-button'}
          type={'primary'}
          roleType={'submit'}
          onClick={handleSubmit}
          isDisabled={newPaymentStore.isLoading}
        >
          {i18n.getMessage('templates.form.button.next')}
        </Button>
      </div>
    </form>
  );
};

BankDetailsStep.propTypes = {
  customerNumber: PropTypes.string,
  userAccounts: PropTypes.array,
  newPaymentStore: MobXPropTypes.observableObject,
  userStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  newPaymentStore: stores.newPaymentStore,
  userStore: stores.userStore
}))(observer(BankDetailsStep));
